import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { addToOrder, getPostPurchaseOffers } from '../api/lastupsell';
import { APIError, EditOrderAPIResponse, NetworkError } from '../api/types';
import {
  CheckoutOrder,
  EditOrderOffer,
  LastUpsellSettings,
  LineItem,
  MoneyFormats,
  Offer,
  PostPurchaseOffersResponseBody,
  MoneyBag,
} from '../types';
import { logError, logInfo } from '../utils/logging';
import captureException from '../utils/sentry';
import { useProductsContext } from './ProductsContext';
import { createContext } from '../dissolve/utils';
import { REGISTERED_APPS } from '../dissolve/types';
import { waitForShopifyCheckoutProperty } from './utils';

export interface UpsellContext {
  addOffer(offer: Offer): Promise<boolean>;

  removeOffer(offer: Offer): Promise<void>;

  acceptOffer(): Promise<EditOrderAPIResponse | APIError | NetworkError | null>;

  declineOffer(): Promise<void>;

  readonly offers?: Offer[];
  readonly settings?: LastUpsellSettings;
  readonly money_formats?: MoneyFormats;
  readonly currency?: string;
}

export interface OrderContext {
  readonly orderId?: number;
  readonly orderName?: string;
  readonly lineItems?: LineItem[];
  readonly subtotalPrice?: string;
  readonly totalPrice?: string;
  readonly totalPriceSet?: MoneyBag;
}

export interface PostPurchaseContext extends UpsellContext, OrderContext {}

export function isOrderStatusPage(): boolean {
  return !!window.Shopify.Checkout;
}

function normalizeLineItemPrices(lineItems: LineItem[] = []) {
  return lineItems.map((item) => {
    const newItem = { ...item };
    if (typeof item.line_price === 'number') {
      newItem.line_price = (item.line_price as any).toFixed(2);
    }
    if (typeof item.price === 'number') {
      newItem.price = (item.price as any).toFixed(2);
    }
    return newItem;
  });
}

const getReferenceIdFromSession = (): string | null => {
  const checkouts = JSON.parse(
    sessionStorage.getItem('checkoutsCompleted') || '[]',
  );
  if (checkouts && checkouts.length > 0) {
    return checkouts[checkouts.length - 1];
  }
  return null;
};

export async function getOrderStatusCheckoutData(): Promise<
  OrderContext & {
    presentmentCurrency: string;
  }
> {
  if (!isOrderStatusPage()) throw Error('not in order status');

  return waitForShopifyCheckoutProperty().then(() => {
    const {
      order_id,
      line_items,
      subtotal_price,
      total_price,
      total_price_set,
      presentment_currency,
    } = window.Shopify.checkout as CheckoutOrder;

    let filteredLineItems = line_items.filter(
      (item) => item.product_id !== null && item.product_id !== undefined,
    );

    filteredLineItems = normalizeLineItemPrices(filteredLineItems);

    return {
      orderId: order_id,
      lineItems: filteredLineItems,
      subtotalPrice: subtotal_price,
      totalPrice: total_price,
      presentmentCurrency: presentment_currency,
      totalPriceSet: total_price_set,
    };
  });
}

const [usePostPurchaseContext, ContextProvider, ContextConsumer] =
  createContext<PostPurchaseContext>();

interface Props {
  children: React.ReactNode;
  orderName?: string;
}

const PostPurchaseContextProvider = ({ children, orderName }: Props) => {
  const { removeOffersWithUnavailableProducts } = useProductsContext();
  const [offers, setOffers] = useState<Offer[]>([]);
  const [offersState, setOffersState] =
    useState<PostPurchaseOffersResponseBody>();
  const [orderState, setOrderState] = useState<OrderContext>();

  useEffect(() => {
    async function loadOffers() {
      try {
        const ppeReferenceId = getReferenceIdFromSession();
        const {
          orderId,
          lineItems,
          subtotalPrice,
          totalPrice,
          presentmentCurrency,
          totalPriceSet,
        } = await getOrderStatusCheckoutData();
        setOrderState({
          orderId,
          lineItems,
          subtotalPrice,
          totalPrice,
          totalPriceSet,
        });

        if (lineItems && lineItems?.length !== 0 && orderId) {
          const response = await getPostPurchaseOffers(
            orderId,
            lineItems,
            ppeReferenceId,
            totalPriceSet,
          );
          if (!response) {
            logInfo('was uninstalled.', REGISTERED_APPS.LAST_UPSELL);
            return;
          }
          const {
            offers: offersFromResponse,
            settings,
            currency,
            money_formats,
          } = {
            ...response,
          };

          let filtered = offersFromResponse;
          if (offersFromResponse) {
            filtered = await removeOffersWithUnavailableProducts(
              offersFromResponse,
            );
          }

          setOffersState({
            offers: filtered,
            settings,
            currency: presentmentCurrency ? presentmentCurrency : currency,
            money_formats,
          });
        }
      } catch (error) {
        logError(
          'Error while fetching offers',
          REGISTERED_APPS.LAST_UPSELL,
          error,
        );
        captureException(
          error instanceof Error
            ? error
            : new Error('Error while fetching offers'),
        );
      }
    }

    loadOffers();
  }, [removeOffersWithUnavailableProducts]);

  const addOffer = async (offer: Offer) => {
    setOffers((_offers) => [..._offers, offer]);
    return true;
  };

  const removeOffer = async (offer: Offer, variantId?: number) => {
    setOffers((_offers) =>
      _offers.filter((_offer) => {
        return !(
          _offer.id === offer.id &&
          _offer.shopify_product_variant_id === variantId
        );
      }),
    );
  };

  const acceptOffer = useCallback(async () => {
    if (!orderState) return null;
    const { orderId } = orderState;
    if (!orderId) return null;

    if (!offers.length) {
      return null;
    }
    const response = await addToOrder(
      orderId,
      offers.map(
        (offer) =>
          ({
            offer_id: offer.id,
            variant_id: offer.shopify_product_variant_id,
            quantity: offer.quantity,
            note: offer.note,
          } as EditOrderOffer),
      ),
    );
    return response;
  }, [orderState, offers]);

  const declineOffer = async () => Promise.resolve();

  const providerValue: PostPurchaseContext = useMemo(() => {
    let value: PostPurchaseContext = {
      addOffer,
      removeOffer,
      acceptOffer,
      declineOffer,
    };

    if (offersState) {
      value = {
        ...value,
        offers: offersState.offers,
        settings: offersState.settings,
        money_formats: offersState.money_formats,
        currency: offersState.currency,
      };
    }

    if (orderState) {
      value = {
        ...value,
        orderId: orderState.orderId,
        orderName,
        lineItems: orderState.lineItems,
        subtotalPrice: orderState.subtotalPrice,
        totalPrice: orderState.totalPrice,
      };
    }

    return value;
  }, [acceptOffer, offersState, orderState, orderName]);

  return <ContextProvider value={providerValue}>{children}</ContextProvider>;
};

const PostPurchaseContextConsumer =
  ContextConsumer as React.Consumer<PostPurchaseContext>;

export {
  usePostPurchaseContext,
  PostPurchaseContextProvider,
  PostPurchaseContextConsumer,
};
